import React, { useEffect, useRef, useState } from "react";
import { useTheme } from "styled-components";
import { useTranslation } from "react-i18next";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore react-pdf v6 ships JS only, no bundled types
import { Document, Page, pdfjs } from "react-pdf";

import {
  SBuildIcon,
  SCircularProgress,
  SIconWrapper,
  SContainer,
  SHeading,
  SLink,
  SPdfContainer,
  SPdfLoading,
  SPdfWrapper,
  SText,
  LEARN_TO_USE_PDF_PAGE_WIDTH,
} from "./styles";

pdfjs.GlobalWorkerOptions.workerSrc = `${
  process.env.PUBLIC_URL || ""
}/pdf.worker.min.js`;

export const LearnToUse: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const pdfUrl = process.env.REACT_APP_LEARN_TO_USE_PDF_URL;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [numPages, setNumPages] = useState(0);
  const [pageWidth, setPageWidth] = useState(LEARN_TO_USE_PDF_PAGE_WIDTH);

  useEffect(() => {
    if (!pdfUrl) return undefined;

    let timeout: ReturnType<typeof setTimeout>;

    const update = (): void => {
      clearTimeout(timeout);

      timeout = setTimeout(() => {
        if (!wrapperRef.current) return;

        const nextWidth = wrapperRef.current.clientWidth;

        setPageWidth((prev) => (prev !== nextWidth ? nextWidth : prev));
      }, 80);
    };

    update();

    const ro = new ResizeObserver(update);

    if (wrapperRef.current) {
      ro.observe(wrapperRef.current);
    }

    return () => {
      ro.disconnect();
      clearTimeout(timeout);
    };
  }, [pdfUrl]);

  if (pdfUrl) {
    return (
      <SPdfContainer>
        <SPdfWrapper ref={wrapperRef}>
          <Document
            file={pdfUrl}
            loading={
              <SPdfLoading>
                <SCircularProgress color="inherit" />
              </SPdfLoading>
            }
            onLoadSuccess={({ numPages: n }: { numPages: number }): void =>
              setNumPages(n)
            }
            onLoadError={(error: Error): void => {
              console.error("[LearnToUse] PDF load error:", error);
            }}
            onSourceError={(error: Error): void => {
              console.error("[LearnToUse] PDF source error:", error);
            }}
          >
            {Array.from({ length: numPages }, (_, i) => (
              <Page
                key={`${i}-${pageWidth}`}
                pageNumber={i + 1}
                width={pageWidth}
                devicePixelRatio={Math.max(window.devicePixelRatio || 1, 2)}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                loading={
                  <SPdfLoading>
                    <SCircularProgress color="inherit" />
                  </SPdfLoading>
                }
              />
            ))}
          </Document>
        </SPdfWrapper>
      </SPdfContainer>
    );
  }

  const formatMessageContact = (): JSX.Element => {
    const { email, whatsapp } = theme.learnToUse;
    const parts = t("learnToUse.messagePart2").split(/{}/g);

    return (
      <>
        {parts[0]}
        <SLink target="_blank" href={`mailto:${email}`}>
          {t("contact.email")}
        </SLink>
        {parts[1]}
        <SLink
          target="_blank"
          href={`${process.env.REACT_APP_WHATSAPP_LINK_DOMAIN}${whatsapp}`}
        >
          {t("contact.whatsApp")}
        </SLink>
        {parts[2]}
      </>
    );
  };

  return (
    <SContainer>
      <SIconWrapper>
        <SBuildIcon />
      </SIconWrapper>

      <SHeading>{t("learnToUse.title")}</SHeading>

      <SText>
        {t("learnToUse.messagePart1")} <br />
        {formatMessageContact()} <br />
        {t("learnToUse.messagePart3")}
      </SText>
    </SContainer>
  );
};
